import { AnyAction, createAction } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import { catchError, combineLatest, concatMap, filter, map, Observable, of, switchMap } from 'rxjs';
import { E_REQUIRE_CAMERA_PERMISSION } from '../../../../Utils/Errors';
import { MediaDeviceVideoSource } from '../../../VideoSource/MediaDeviceVideoSource';
import ComponentStateActions from '../../Global/ComponentState/ComponentStateActions';
import { StoreDependencies } from '../../Global/StoreDependencies';
import { StoreState } from '../../Global/StoreState';
import VideoStoreActions from '../../Global/Video/VideoStoreActions';
import MediaDeviceMenuActions from './MediaDeviceMenuActions';

const setMediaSourceAction = createAction<MediaDeviceInfo>('MEDIA_DEVICE_MENU_SET_VIDEO_SOURCE');

function getAvailableMediaDevices(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(MediaDeviceMenuActions.getAvailableMediaDevices.match),
    switchMap(() => dependencies.mediaDeviceFinder!.getAvailableMediaInfos()),
    map(MediaDeviceMenuActions.setAvailableMediaDevices),
    catchError(() => of(ComponentStateActions.setError('MEDIA_DEVICE_MENU', E_REQUIRE_CAMERA_PERMISSION)))
  );
}

function selectMediaDevice(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  _dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return combineLatest([$action, $state]).pipe(
    map(([action, state]) => ({ action, state })),
    filter(({ action }) => MediaDeviceMenuActions.selectMediaDeviceWithId.match(action)),
    map(({ action, state }) =>
      state.mediaDeviceMenu.availableMediaDevices.find((mediaDevice) => mediaDevice.deviceId === action.payload)
    ),
    filter((mediaDevice) => !!mediaDevice),
    concatMap((mediaDevice) => [
      setMediaSourceAction(mediaDevice!),
      MediaDeviceMenuActions.setSelectedMediaDevice(mediaDevice!)
    ])
  );
}

function setMediaSource(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(setMediaSourceAction.match),
    map(({ payload }) => payload.deviceId),
    concatMap(dependencies.mediaDeviceFinder!.getMediaStreamForDeviceWithId),
    map((mediaStream) => new MediaDeviceVideoSource(mediaStream)),
    map(VideoStoreActions.setVideoSource)
  );
}

export default combineEpics(getAvailableMediaDevices, selectMediaDevice, setMediaSource);
